import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Filter, ArrowRight } from 'lucide-react';
import './ChatComponents.css';

/**
 * Sortable, filterable table for framework gaps
 */
function GapTable({ title, data, actions, onAction }) {
  const [sortField, setSortField] = useState('priority');
  const [sortDir, setSortDir] = useState('asc');
  const [filterPriority, setFilterPriority] = useState('all');
  const [expanded, setExpanded] = useState({});

  const priorityOrder = { high: 1, medium: 2, low: 3 };

  const sortedRows = useMemo(() => {
    let rows = [...(data.rows || [])];

    // Filter
    if (filterPriority !== 'all') {
      rows = rows.filter(r => r.priority === filterPriority);
    }

    // Sort
    rows.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'priority') {
        aVal = priorityOrder[aVal] || 99;
        bVal = priorityOrder[bVal] || 99;
      }

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return rows;
  }, [data.rows, sortField, sortDir, filterPriority]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const toggleRow = (idx) => {
    setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleRowAction = (row) => {
    if (actions?.[0]?.queryTemplate && onAction) {
      const query = actions[0].queryTemplate
        .replace('{control_id}', row.control_id)
        .replace('{control_name}', row.control_name);
      onAction({ type: 'query', query });
    }
  };

  const getPriorityColor = (priority) => {
    return data.priorityColors?.[priority] || 'var(--color-text-muted)';
  };

  return (
    <div className="cc-table-container">
      {title && <div className="cc-table-title">{title}</div>}

      {/* Filters */}
      {data.filterable && (
        <div className="cc-table-filters">
          <Filter size={14} />
          <select
            value={filterPriority}
            onChange={e => setFilterPriority(e.target.value)}
            className="cc-filter-select"
          >
            <option value="all">All priorities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      )}

      {/* Table */}
      <div className="cc-table-scroll">
        <table className="cc-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('priority')} className="cc-th-sortable">
                Priority
                {sortField === 'priority' && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
              </th>
              <th onClick={() => handleSort('control_id')} className="cc-th-sortable">
                Control
                {sortField === 'control_id' && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
              </th>
              <th>Domain</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, idx) => (
              <React.Fragment key={idx}>
                <tr className="cc-tr-clickable" onClick={() => toggleRow(idx)}>
                  <td>
                    <span
                      className="cc-priority-badge"
                      style={{ backgroundColor: getPriorityColor(row.priority) }}
                    >
                      {row.priority}
                    </span>
                  </td>
                  <td>
                    <div className="cc-control-cell">
                      <span className="cc-control-id">{row.control_id}</span>
                      <span className="cc-control-name">{row.control_name}</span>
                    </div>
                  </td>
                  <td>{row.domain}</td>
                  <td>
                    <button
                      className="cc-action-btn"
                      onClick={(e) => { e.stopPropagation(); handleRowAction(row); }}
                      title="Find products"
                    >
                      <ArrowRight size={14} />
                    </button>
                  </td>
                </tr>
                {expanded[idx] && row.recommended_products?.length > 0 && (
                  <tr className="cc-expanded-row">
                    <td colSpan={4}>
                      <div className="cc-recommendations">
                        <strong>Recommended products:</strong>
                        {row.recommended_products.map((p, i) => (
                          <span key={i} className="cc-rec-product">
                            {p.product_name || p.product_id}
                            <span className="cc-rec-meta">
                              ({p.mapping_strength}, {p.setup_complexity} setup)
                            </span>
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="cc-table-footer">
        Showing {sortedRows.length} of {data.rows?.length || 0} gaps
      </div>
    </div>
  );
}

export default GapTable;
